import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  BeachAccess as BeachAccessIcon,
  CalendarMonth as CalendarMonthIcon,
  CheckCircle as CheckCircleIcon,
  EventBusy as EventBusyIcon,
  Group as GroupIcon,
  People as PeopleIcon,
  PlaylistAddCheck as PlaylistAddCheckIcon,
  WarningAmber as WarningAmberIcon,
  Work as WorkIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { anomaliesAPI, checkoutAdminAPI, groupDashboardAPI, leaveAdminAPI } from '../services/api';

interface DashboardStats {
  total_dipendenti: number;
  al_lavoro: number;
  contratto_cessato: number;
  contratto_in_scadenza: number;
  in_permesso: number;
  in_ferie: number;
}

interface AnomalySummary {
  total: number;
  severe: number;
  moderate: number;
  minor: number;
  employees: AnomalyEmployee[];
}

interface AnomalyEmployee {
  employee_id: number;
  user_id?: number;
  anno?: number;
  mese?: number;
  nome: string;
  cognome: string;
  codice_fiscale: string;
  dipartimento: string;
  ore_previste: number;
  ore_effettive: number;
  differenza: number;
  percentuale_completamento: number;
  gravita: 'severe' | 'moderate' | 'minor';
}

interface GroupSummary {
  id: number;
  name: string;
  description?: string;
  admin_manages_timesheets?: boolean;
  employeeCount: number;
  employees: GroupEmployee[];
}

interface GroupEmployee {
  id: number;
  user_id?: number;
  nome: string;
  cognome: string;
  codice_fiscale?: string;
  matricola?: string;
  dipartimento?: string;
  email?: string;
}

interface LeaveRequest {
  id: number;
  employee_id: number;
  employee_name: string;
  employee_surname: string;
  request_type: 'ferie' | 'permesso' | 'malattia' | 'altri';
  start_date: string;
  end_date?: string;
  duration_hours?: number;
  duration_days?: number;
  status?: 'pending' | 'approved' | 'rejected';
}

interface PendingCheckout {
  id?: number;
  presence_id?: number;
  user_id?: number;
  nome?: string;
  cognome?: string;
  check_in_time?: string;
}

interface PendingValidationEmployee {
  employee_id: number;
  user_id?: number;
  nome: string;
  cognome: string;
  matricola?: string;
  anomaly_status: 'clean' | 'minor' | 'significant';
  anomalies_count: number;
  can_validate: boolean;
}

interface GroupDashboardSummaryResponse {
  stats?: DashboardStats;
  daily_anomalies?: AnomalySummary;
  weekly_anomalies?: AnomalySummary;
  monthly_anomalies?: AnomalySummary;
  pending_leave_requests?: LeaveRequest[];
  pending_checkouts?: PendingCheckout[];
  pending_validations?: PendingValidationEmployee[];
  groups?: Array<{
    id: number;
    name: string;
    description?: string;
    admin_manages_timesheets?: boolean;
    employee_count?: number;
    employees?: GroupEmployee[];
  }>;
  visible_employee_count?: number;
  reference_year?: number;
  reference_month?: number;
}

const defaultStats: DashboardStats = {
  total_dipendenti: 0,
  al_lavoro: 0,
  contratto_cessato: 0,
  contratto_in_scadenza: 0,
  in_permesso: 0,
  in_ferie: 0,
};

const defaultAnomalies: AnomalySummary = {
  total: 0,
  severe: 0,
  moderate: 0,
  minor: 0,
  employees: [],
};

const StatCard: React.FC<{
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
  onClick?: () => void;
}> = ({ title, value, icon, color, subtitle, onClick }) => (
  <Card
    onClick={onClick}
    sx={{
      height: '100%',
      borderLeft: `4px solid ${color}`,
      cursor: onClick ? 'pointer' : 'default',
      transition: 'all 0.2s ease',
      '&:hover': onClick ? { boxShadow: 6, transform: 'translateY(-2px)' } : undefined,
    }}
  >
    <CardContent>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Box sx={{ color }}>{icon}</Box>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
      </Box>
      <Typography variant="h4" sx={{ fontWeight: 'bold', color }}>
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const GroupAdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isAdmin, isSuperAdmin, isGroupAdmin, loadingGroupCheck } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>(defaultStats);
  const [dailyAnomalies, setDailyAnomalies] = useState<AnomalySummary>(defaultAnomalies);
  const [weeklyAnomalies, setWeeklyAnomalies] = useState<AnomalySummary>(defaultAnomalies);
  const [monthlyAnomalies, setMonthlyAnomalies] = useState<AnomalySummary>(defaultAnomalies);
  const [groups, setGroups] = useState<GroupSummary[]>([]);
  const [visibleEmployeeCount, setVisibleEmployeeCount] = useState(0);
  const [pendingLeaveRequests, setPendingLeaveRequests] = useState<LeaveRequest[]>([]);
  const [pendingCheckouts, setPendingCheckouts] = useState<PendingCheckout[]>([]);
  const [pendingValidations, setPendingValidations] = useState<PendingValidationEmployee[]>([]);
  const [referenceYear, setReferenceYear] = useState(new Date().getFullYear());
  const [referenceMonth, setReferenceMonth] = useState(new Date().getMonth() + 1);
  const [employeesDialogOpen, setEmployeesDialogOpen] = useState(false);
  const [employeesDialogTitle, setEmployeesDialogTitle] = useState('');
  const [employeesDialogRows, setEmployeesDialogRows] = useState<GroupEmployee[]>([]);
  const [anomaliesDialogOpen, setAnomaliesDialogOpen] = useState(false);
  const [anomaliesDialogTitle, setAnomaliesDialogTitle] = useState('');
  const [selectedAnomalies, setSelectedAnomalies] = useState<AnomalyEmployee[]>([]);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(false);
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<GroupSummary | null>(null);

  const loadDashboard = useCallback(async () => {
    if (!isAdmin || isSuperAdmin || !isGroupAdmin) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const summary: GroupDashboardSummaryResponse = await groupDashboardAPI.getSummary().catch(() => ({}));
      const mappedGroups = (summary.groups || []).map((group) => ({
        id: group.id,
        name: group.name,
        description: group.description,
        admin_manages_timesheets: group.admin_manages_timesheets || false,
        employeeCount: group.employee_count ?? group.employees?.length ?? 0,
        employees: group.employees || [],
      }));

      setGroups(mappedGroups);
      setVisibleEmployeeCount(summary.visible_employee_count || 0);
      setStats(summary.stats || defaultStats);
      setDailyAnomalies(summary.daily_anomalies || defaultAnomalies);
      setWeeklyAnomalies(summary.weekly_anomalies || defaultAnomalies);
      setMonthlyAnomalies(summary.monthly_anomalies || defaultAnomalies);
      setPendingLeaveRequests(summary.pending_leave_requests || []);
      setPendingCheckouts(summary.pending_checkouts || []);
      setPendingValidations(summary.pending_validations || []);
      setReferenceYear(summary.reference_year || new Date().getFullYear());
      setReferenceMonth(summary.reference_month || new Date().getMonth() + 1);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, isSuperAdmin, isGroupAdmin]);

  useEffect(() => {
    loadDashboard();

    const interval = window.setInterval(loadDashboard, 300000);
    return () => window.clearInterval(interval);
  }, [loadDashboard]);

  if (loadingGroupCheck || loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/employee-dashboard" replace />;
  }

  if (isSuperAdmin || !isGroupAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  const managedGroupsCount = groups.filter((group) => group.admin_manages_timesheets).length;
  const allVisibleEmployees = groups.flatMap((group) => group.employees);

  const getYearMonthFromDate = (dateValue?: string) => {
    const referenceDate = dateValue ? new Date(dateValue) : new Date();
    return {
      year: referenceDate.getFullYear(),
      month: referenceDate.getMonth() + 1,
    };
  };

  const getRequestTypeLabel = (type: string) => {
    switch (type) {
      case 'ferie':
        return 'Ferie';
      case 'permesso':
        return 'Permesso';
      case 'malattia':
        return 'Malattia';
      case 'altri':
        return 'Altri';
      default:
        return type;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'severe':
        return 'error';
      case 'moderate':
        return 'warning';
      case 'minor':
        return 'info';
      default:
        return 'default';
    }
  };

  const getSeverityLabel = (severity: string) => {
    switch (severity) {
      case 'severe':
        return 'Critica';
      case 'moderate':
        return 'Media';
      case 'minor':
        return 'Lieve';
      default:
        return 'N/A';
    }
  };

  const openEmployeesDialog = async (title: string, loader: () => Promise<GroupEmployee[]>) => {
    const rows = await loader();
    setEmployeesDialogTitle(title);
    setEmployeesDialogRows(rows);
    setEmployeesDialogOpen(true);
  };

  const handleVisibleEmployeesClick = () => {
    setEmployeesDialogTitle('Dipendenti visibili nel tuo perimetro');
    setEmployeesDialogRows(allVisibleEmployees);
    setEmployeesDialogOpen(true);
  };

  const handleStatCardClick = async (category: 'al-lavoro' | 'in-permesso' | 'in-ferie') => {
    if (category === 'al-lavoro') {
      await openEmployeesDialog('Dipendenti al lavoro', async () => {
        const response = await anomaliesAPI.getEmployeesAlLavoro();
        return response?.employees || response || [];
      });
      return;
    }

    if (category === 'in-permesso') {
      await openEmployeesDialog('Dipendenti in permesso', async () => {
        const response = await anomaliesAPI.getEmployeesInPermesso();
        return response?.employees || [];
      });
      return;
    }

    await openEmployeesDialog('Dipendenti in ferie', async () => {
      const response = await anomaliesAPI.getEmployeesInFerie();
      return response?.employees || [];
    });
  };

  const handleAnomaliesClick = (title: string, anomalies: AnomalyEmployee[]) => {
    setAnomaliesDialogTitle(title);
    setSelectedAnomalies(anomalies);
    setAnomaliesDialogOpen(true);
  };

  const handleOpenGroup = (group: GroupSummary) => {
    setSelectedGroup(group);
    setGroupDialogOpen(true);
  };

  const handleApproveRequest = async (requestId: number) => {
    await leaveAdminAPI.approveRequest(requestId, { status: 'approved', approver_notes: '' });
    await loadDashboard();
  };

  const handleRejectRequest = async (requestId: number) => {
    await leaveAdminAPI.approveRequest(requestId, { status: 'rejected', approver_notes: '' });
    await loadDashboard();
  };

  const handleForceCheckout = async (presenceId: number) => {
    await checkoutAdminAPI.forceCheckout(presenceId);
    await loadDashboard();
  };

  const handleAutoCheckout = async () => {
    if (!window.confirm('Vuoi eseguire il checkout automatico per tutte le presenze con più di 12 ore?')) {
      return;
    }

    await checkoutAdminAPI.autoCheckoutExpired(12);
    await loadDashboard();
  };

  const openEmployeeTimesheet = (employeeId?: number, userId?: number, referenceDate?: string) => {
    const { year, month } = getYearMonthFromDate(referenceDate);
    const params = new URLSearchParams();
    if (employeeId) {
      params.set('employee_id', String(employeeId));
    }
    if (userId) {
      params.set('user_id', String(userId));
    }
    params.set('year', String(year));
    params.set('month', String(month));
    navigate(`/admin-timesheets?${params.toString()}`);
  };

  const openRequestInHistory = (request: LeaveRequest) => {
    const params = new URLSearchParams();
    params.set('status', 'pending');
    params.set('request_type', request.request_type);
    params.set('employee', `${request.employee_name} ${request.employee_surname}`.trim());
    navigate(`/leave/history?${params.toString()}`);
  };

  const openPendingValidation = (employee: PendingValidationEmployee) => {
    if (!employee.user_id) {
      return;
    }

    const params = new URLSearchParams();
    params.set('user_id', String(employee.user_id));
    params.set('year', String(referenceYear));
    params.set('month', String(referenceMonth));
    navigate(`/presence-validation?${params.toString()}`);
  };

  const getValidationStatusLabel = (status: PendingValidationEmployee['anomaly_status']) => {
    switch (status) {
      case 'significant':
        return 'Anomalie significative';
      case 'minor':
        return 'Anomalie minori';
      case 'clean':
      default:
        return 'Da validare';
    }
  };

  const getValidationStatusColor = (status: PendingValidationEmployee['anomaly_status']) => {
    switch (status) {
      case 'significant':
        return 'error';
      case 'minor':
        return 'warning';
      case 'clean':
      default:
        return 'success';
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#0d47a1', mb: 1 }}>
        Dashboard Amministratore di Gruppo
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Panoramica operativa limitata ai dipendenti dei gruppi che amministri.
      </Typography>

      <Card sx={{ mb: 3, borderLeft: '4px solid #0d47a1' }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#0d47a1', mb: 1 }}>
            Benvenuto nell'area amministrativa di gruppo
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {user?.nome
              ? `${user.nome} ${user.cognome || ''}`.trim()
              : 'Utente amministrativo'}
            {' '}sta operando sui gruppi assegnati, con visibilita limitata al proprio perimetro organizzativo.
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Gruppi assegnati:</strong> {groups.length}
            </Typography>
            <Typography variant="body2">
              <strong>Dipendenti nel perimetro:</strong> {visibleEmployeeCount}
            </Typography>
            <Typography variant="body2">
              <strong>Gruppi con gestione cartellini admin:</strong> {managedGroupsCount}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label="Contesto: amministrazione di gruppo" size="small" color="primary" variant="outlined" />
            <Chip label="Ambito: dipendenti assegnati" size="small" color="primary" variant="outlined" />
            <Chip label="Focus: controllo operativo quotidiano" size="small" color="primary" variant="outlined" />
          </Stack>
        </CardContent>
      </Card>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
        <Chip icon={<GroupIcon />} label={`${groups.length} gruppi amministrati`} color="primary" variant="outlined" />
        <Chip icon={<PeopleIcon />} label={`${visibleEmployeeCount} dipendenti visibili`} color="primary" variant="outlined" />
        {managedGroupsCount > 0 && (
          <Chip
            icon={<PlaylistAddCheckIcon />}
            label={`${managedGroupsCount} gruppi con gestione cartellini admin`}
            color="secondary"
            variant="outlined"
          />
        )}
      </Stack>

      <Alert severity="info" sx={{ mb: 4 }}>
        I conteggi, le anomalie, le richieste ferie e le azioni disponibili sono riferiti esclusivamente ai dipendenti assegnati ai tuoi gruppi.
      </Alert>

      <Box mb={4}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
          Panoramica del tuo perimetro
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4} lg={4}>
            <StatCard
              title="Dipendenti visibili"
              value={stats.total_dipendenti || visibleEmployeeCount}
              icon={<PeopleIcon />}
              color="#0d47a1"
              subtitle="Clicca per l'elenco completo"
              onClick={handleVisibleEmployeesClick}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={4}>
            <StatCard
              title="Al lavoro"
              value={stats.al_lavoro || 0}
              icon={<WorkIcon />}
              color="#4caf50"
              subtitle="Clicca per vedere i dipendenti"
              onClick={() => handleStatCardClick('al-lavoro')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={4}>
            <StatCard
              title="In permesso"
              value={stats.in_permesso || 0}
              icon={<EventBusyIcon />}
              color="#2196f3"
              subtitle="Clicca per vedere i dipendenti"
              onClick={() => handleStatCardClick('in-permesso')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={6}>
            <StatCard
              title="In ferie"
              value={stats.in_ferie || 0}
              icon={<BeachAccessIcon />}
              color="#00bcd4"
              subtitle="Clicca per vedere i dipendenti"
              onClick={() => handleStatCardClick('in-ferie')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={6}>
            <StatCard
              title="Checkout pendenti"
              value={pendingCheckouts.length}
              icon={<AccessTimeIcon />}
              color="#ff5722"
              subtitle="Clicca per la gestione rapida"
              onClick={() => setCheckoutDialogOpen(true)}
            />
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 4 }} />

      <Box mb={4}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
          Azioni urgenti
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Richieste ferie in attesa
                </Typography>
                <Typography variant="h4" color="#9c27b0" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {pendingLeaveRequests.length}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Approvazioni dei dipendenti del tuo perimetro.
                </Typography>
                <Button fullWidth variant="outlined" onClick={() => setLeaveDialogOpen(true)}>
                  Apri richieste ferie
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Anomalie giornaliere
                </Typography>
                <Typography variant="h4" color="#f44336" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {dailyAnomalies.total || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Dipendenti con criticità rilevate oggi.
                </Typography>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => handleAnomaliesClick('Anomalie giornaliere', dailyAnomalies.employees || [])}
                >
                  Vai alle anomalie
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Cartellini del mese da controllare
                </Typography>
                <Typography variant="h4" color="#ff9800" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {monthlyAnomalies.total || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Situazioni mensili da correggere o verificare.
                </Typography>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => handleAnomaliesClick('Anomalie mensili', monthlyAnomalies.employees || [])}
                >
                  Apri cartellini
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Validazioni pendenti
                </Typography>
                <Typography variant="h4" color="#2e7d32" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {pendingValidations.length}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Cartellini del mese corrente ancora da validare nel tuo perimetro.
                </Typography>
                <Button fullWidth variant="outlined" onClick={() => setValidationDialogOpen(true)}>
                  Apri validazioni
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Inserimento presenze
                </Typography>
                <Typography variant="h4" color="#673ab7" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {managedGroupsCount}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Gruppi con compilazione cartellini gestita direttamente dagli admin.
                </Typography>
                <Button fullWidth variant="contained" disabled={managedGroupsCount === 0} onClick={() => navigate('/guided-presence')}>
                  Apri inserimento presenze
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 4 }} />

      <Box mb={4}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
          Controllo del gruppo
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <StatCard
              title="Anomalie giornaliere"
              value={dailyAnomalies.total || 0}
              icon={<WarningAmberIcon />}
              color={dailyAnomalies.total > 0 ? '#f44336' : '#4caf50'}
              subtitle="Situazione di oggi"
              onClick={() => handleAnomaliesClick('Anomalie giornaliere', dailyAnomalies.employees || [])}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <StatCard
              title="Anomalie settimanali"
              value={weeklyAnomalies.total || 0}
              icon={<CalendarMonthIcon />}
              color={weeklyAnomalies.total > 0 ? '#ff9800' : '#4caf50'}
              subtitle="Andamento della settimana"
              onClick={() => handleAnomaliesClick('Anomalie settimanali', weeklyAnomalies.employees || [])}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <StatCard
              title="Anomalie mensili"
              value={monthlyAnomalies.total || 0}
              icon={<CheckCircleIcon />}
              color={monthlyAnomalies.total > 0 ? '#ff5722' : '#4caf50'}
              subtitle="Cartellini da verificare"
              onClick={() => handleAnomaliesClick('Anomalie mensili', monthlyAnomalies.employees || [])}
            />
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 4 }} />

      <Grid container spacing={3}>
        <Grid item xs={12} lg={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Gruppi amministrati
              </Typography>
              <List dense disablePadding>
                {groups.length === 0 && (
                  <ListItem disableGutters>
                    <ListItemText primary="Nessun gruppo associato" secondary="Non risultano gruppi amministrati per l'utente corrente." />
                  </ListItem>
                )}
                {groups.map((group) => (
                  <ListItem
                    key={group.id}
                    disableGutters
                    button
                    onClick={() => handleOpenGroup(group)}
                    sx={{ py: 1.5, borderBottom: '1px solid', borderColor: 'divider', cursor: 'pointer' }}
                  >
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {group.name}
                          </Typography>
                          {group.admin_manages_timesheets && (
                            <Chip label="Gestione cartellini admin" size="small" color="secondary" variant="outlined" />
                          )}
                        </Stack>
                      }
                      secondary={`${group.employeeCount} dipendenti assegnati • clicca per il dettaglio`}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Attività recenti da seguire
              </Typography>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Richieste ferie in attesa
              </Typography>
              <List dense disablePadding sx={{ mb: 2 }}>
                {pendingLeaveRequests.length === 0 && (
                  <ListItem disableGutters>
                    <ListItemText primary="Nessuna richiesta in attesa" />
                  </ListItem>
                )}
                {pendingLeaveRequests.slice(0, 5).map((request) => (
                  <ListItem key={request.id} disableGutters>
                    <ListItemText
                      primary={`${request.employee_name} ${request.employee_surname}`}
                      secondary={`${getRequestTypeLabel(request.request_type)} - dal ${new Date(request.start_date).toLocaleDateString('it-IT')}`}
                    />
                  </ListItem>
                ))}
              </List>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Checkout pendenti
              </Typography>
              <List dense disablePadding>
                {pendingCheckouts.length === 0 && (
                  <ListItem disableGutters>
                    <ListItemText primary="Nessun checkout pendente" />
                  </ListItem>
                )}
                {pendingCheckouts.slice(0, 5).map((checkout, index) => (
                  <ListItem key={checkout.presence_id || checkout.id || index} disableGutters>
                    <ListItemText
                      primary={`${checkout.nome || ''} ${checkout.cognome || ''}`.trim() || 'Dipendente'}
                      secondary={checkout.check_in_time ? `Check-in ${new Date(checkout.check_in_time).toLocaleString('it-IT')}` : 'Checkout da verificare'}
                    />
                  </ListItem>
                ))}
              </List>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 1 }}>
                Validazioni del mese in attesa
              </Typography>
              <List dense disablePadding>
                {pendingValidations.length === 0 && (
                  <ListItem disableGutters>
                    <ListItemText primary="Nessun cartellino in attesa di validazione" />
                  </ListItem>
                )}
                {pendingValidations.slice(0, 5).map((employee) => (
                  <ListItem key={employee.employee_id} disableGutters>
                    <ListItemText
                      primary={`${employee.nome} ${employee.cognome}`}
                      secondary={`${getValidationStatusLabel(employee.anomaly_status)}${employee.anomalies_count > 0 ? ` • ${employee.anomalies_count} anomalie` : ''}`}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={employeesDialogOpen} onClose={() => setEmployeesDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {employeesDialogTitle}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            Totale: {employeesDialogRows.length} dipendenti
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 550 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Nome</strong></TableCell>
                  <TableCell><strong>Cognome</strong></TableCell>
                  <TableCell><strong>Codice fiscale</strong></TableCell>
                  <TableCell><strong>Matricola</strong></TableCell>
                  <TableCell><strong>Dipartimento</strong></TableCell>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell align="center"><strong>Azioni</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {employeesDialogRows.map((employee, index) => (
                  <TableRow key={employee.id || index} hover>
                    <TableCell>{employee.nome || '-'}</TableCell>
                    <TableCell>{employee.cognome || '-'}</TableCell>
                    <TableCell>{employee.codice_fiscale || '-'}</TableCell>
                    <TableCell>{employee.matricola || '-'}</TableCell>
                    <TableCell>{employee.dipartimento || '-'}</TableCell>
                    <TableCell>{employee.email || '-'}</TableCell>
                    <TableCell align="center">
                      <Button size="small" variant="outlined" onClick={() => openEmployeeTimesheet(employee.id, employee.user_id)}>
                        Apri cartellino
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmployeesDialogOpen(false)} variant="contained">
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={anomaliesDialogOpen} onClose={() => setAnomaliesDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {anomaliesDialogTitle}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            Totale: {selectedAnomalies.length} dipendenti
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 550 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Dipendente</strong></TableCell>
                  <TableCell><strong>CF</strong></TableCell>
                  <TableCell><strong>Dipartimento</strong></TableCell>
                  <TableCell align="right"><strong>Ore previste</strong></TableCell>
                  <TableCell align="right"><strong>Ore effettive</strong></TableCell>
                  <TableCell align="right"><strong>Differenza</strong></TableCell>
                  <TableCell align="center"><strong>Gravità</strong></TableCell>
                  <TableCell align="center"><strong>Azioni</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedAnomalies.map((employee) => (
                  <TableRow key={employee.employee_id} hover>
                    <TableCell>{employee.nome} {employee.cognome}</TableCell>
                    <TableCell>{employee.codice_fiscale}</TableCell>
                    <TableCell>{employee.dipartimento}</TableCell>
                    <TableCell align="right">{employee.ore_previste}h</TableCell>
                    <TableCell align="right">{employee.ore_effettive}h</TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: employee.differenza < 0 ? '#f44336' : '#4caf50', fontWeight: 'bold' }}
                    >
                      {employee.differenza > 0 ? '+' : ''}{employee.differenza.toFixed(1)}h
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={getSeverityLabel(employee.gravita)} color={getSeverityColor(employee.gravita) as any} size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        {employee.user_id && employee.anno && employee.mese && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => navigate(`/presence-validation?user_id=${employee.user_id}&year=${employee.anno}&month=${employee.mese}`)}
                          >
                            Validazione
                          </Button>
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => navigate('/employees', { state: { focusEmployeeId: employee.employee_id } })}
                        >
                          Dipendente
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => navigate('/anomalies')} variant="outlined">
            Apri sezione anomalie
          </Button>
          <Button onClick={() => setAnomaliesDialogOpen(false)} variant="contained">
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={leaveDialogOpen} onClose={() => setLeaveDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Richieste ferie e permessi in attesa
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            Totale: {pendingLeaveRequests.length} richieste
          </Typography>
        </DialogTitle>
        <DialogContent>
          <List disablePadding sx={{ mt: 1 }}>
            {pendingLeaveRequests.length === 0 && (
              <ListItem disableGutters>
                <ListItemText primary="Nessuna richiesta in attesa" />
              </ListItem>
            )}
            {pendingLeaveRequests.map((request) => (
              <ListItem
                key={request.id}
                disableGutters
                sx={{ py: 1.5, borderBottom: '1px solid', borderColor: 'divider', alignItems: 'flex-start' }}
                secondaryAction={
                  <Stack direction="row" spacing={1}>
                    <Button size="small" variant="outlined" onClick={() => openRequestInHistory(request)}>
                      Apri
                    </Button>
                    <Button size="small" variant="outlined" color="success" onClick={() => handleApproveRequest(request.id)}>
                      Approva
                    </Button>
                    <Button size="small" variant="outlined" color="error" onClick={() => handleRejectRequest(request.id)}>
                      Rifiuta
                    </Button>
                  </Stack>
                }
              >
                <ListItemText
                  primary={`${request.employee_name} ${request.employee_surname}`}
                  secondary={`${getRequestTypeLabel(request.request_type)} dal ${new Date(request.start_date).toLocaleDateString('it-IT')}${request.end_date ? ` al ${new Date(request.end_date).toLocaleDateString('it-IT')}` : ''}`}
                  sx={{ pr: 18 }}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => navigate('/leave/history')} variant="outlined">
            Apri storico ferie
          </Button>
          <Button onClick={() => setLeaveDialogOpen(false)} variant="contained">
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={checkoutDialogOpen} onClose={() => setCheckoutDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Checkout pendenti
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            Totale: {pendingCheckouts.length} presenze
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" justifyContent="flex-end" sx={{ mt: 1, mb: 2 }}>
            <Button variant="outlined" onClick={handleAutoCheckout} disabled={pendingCheckouts.length === 0}>
              Checkout automatico
            </Button>
          </Box>
          <List disablePadding>
            {pendingCheckouts.length === 0 && (
              <ListItem disableGutters>
                <ListItemText primary="Nessun checkout pendente" />
              </ListItem>
            )}
            {pendingCheckouts.map((checkout, index) => (
              <ListItem
                key={checkout.presence_id || checkout.id || index}
                disableGutters
                sx={{ py: 1.5, borderBottom: '1px solid', borderColor: 'divider', alignItems: 'flex-start' }}
                secondaryAction={
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => openEmployeeTimesheet(undefined, checkout.user_id, checkout.check_in_time)}
                      disabled={!checkout.user_id}
                    >
                      Apri cartellino
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => checkout.presence_id && handleForceCheckout(checkout.presence_id)}
                      disabled={!checkout.presence_id}
                    >
                      Forza checkout
                    </Button>
                  </Stack>
                }
              >
                <ListItemText
                  primary={`${checkout.nome || ''} ${checkout.cognome || ''}`.trim() || 'Dipendente'}
                  secondary={checkout.check_in_time ? `Check-in ${new Date(checkout.check_in_time).toLocaleString('it-IT')}` : 'Checkout da verificare'}
                  sx={{ pr: 18 }}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => navigate('/admin-timesheets')} variant="outlined">
            Apri cartellini dipendenti
          </Button>
          <Button onClick={() => setCheckoutDialogOpen(false)} variant="contained">
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={validationDialogOpen} onClose={() => setValidationDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Validazioni pendenti del mese
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            Totale: {pendingValidations.length} cartellini
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
            Elenco riferito al mese {referenceMonth}/{referenceYear} e limitato ai dipendenti del tuo perimetro.
          </Alert>
          <List disablePadding>
            {pendingValidations.length === 0 && (
              <ListItem disableGutters>
                <ListItemText primary="Nessun cartellino in attesa di validazione" />
              </ListItem>
            )}
            {pendingValidations.map((employee) => (
              <ListItem
                key={employee.employee_id}
                disableGutters
                sx={{ py: 1.5, borderBottom: '1px solid', borderColor: 'divider', alignItems: 'flex-start' }}
                secondaryAction={
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => openPendingValidation(employee)}
                      disabled={!employee.user_id}
                    >
                      Apri validazione
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => openEmployeeTimesheet(employee.employee_id, employee.user_id)}
                    >
                      Apri cartellino
                    </Button>
                  </Stack>
                }
              >
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {employee.nome} {employee.cognome}
                      </Typography>
                      <Chip
                        label={getValidationStatusLabel(employee.anomaly_status)}
                        color={getValidationStatusColor(employee.anomaly_status) as 'success' | 'warning' | 'error'}
                        size="small"
                        variant="outlined"
                      />
                    </Stack>
                  }
                  secondary={`Matricola: ${employee.matricola || '-'}${employee.anomalies_count > 0 ? ` • anomalie rilevate: ${employee.anomalies_count}` : ' • nessuna anomalia bloccante rilevata'}`}
                  sx={{ pr: 24 }}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => navigate('/presence-validation')} variant="outlined">
            Apri sezione validazioni
          </Button>
          <Button onClick={() => setValidationDialogOpen(false)} variant="contained">
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={groupDialogOpen} onClose={() => setGroupDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedGroup?.name || 'Gruppo'}
          {selectedGroup?.admin_manages_timesheets && (
            <Chip label="Gestione cartellini admin" size="small" color="secondary" variant="outlined" sx={{ ml: 2 }} />
          )}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
            Dipendenti assegnati: {selectedGroup?.employeeCount || 0}
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 550 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Nome</strong></TableCell>
                  <TableCell><strong>Cognome</strong></TableCell>
                  <TableCell><strong>Codice fiscale</strong></TableCell>
                  <TableCell><strong>Dipartimento</strong></TableCell>
                  <TableCell align="center"><strong>Azioni</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(selectedGroup?.employees || []).map((employee, index) => (
                  <TableRow key={employee.id || index} hover>
                    <TableCell>{employee.nome || '-'}</TableCell>
                    <TableCell>{employee.cognome || '-'}</TableCell>
                    <TableCell>{employee.codice_fiscale || '-'}</TableCell>
                    <TableCell>{employee.dipartimento || '-'}</TableCell>
                    <TableCell align="center">
                      <Button size="small" variant="outlined" onClick={() => openEmployeeTimesheet(employee.id, employee.user_id)}>
                        Apri cartellino
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => navigate('/guided-presence')} variant="outlined" disabled={!selectedGroup?.admin_manages_timesheets}>
            Vai a inserimento presenze
          </Button>
          <Button onClick={() => setGroupDialogOpen(false)} variant="contained">
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GroupAdminDashboard;
